import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import {
  CatalogProduct,
  Hybrid,
  IConfig,
  ISubscription,
  Manufacturer,
  Nutrition,
  Protection,
  ProtectionProduct,
  User,
  type ManufacturerDrone,
} from './types';

type AppDataStore = {
  subscription: ISubscription;
  subscriptions: ISubscription[];
  user: User;
  isLoading: boolean;
  config: IConfig;
  setCultureType: (cultureType: string) => void;

  listNutrition: Nutrition[];
  itemNutritionSelected: Nutrition;
  setItemNutritionSelected: (item: Nutrition) => void;

  listProtection: Protection[];
  itemProtectionSelected: Protection;
  setItemProtectionSelected: (item: Protection) => void;

  hybrids: Hybrid[];
  loadHybrids: (culture_type: string) => Promise<void>;

  manufacturers: Manufacturer[];
  loadManufacturers: () => Promise<void>;

  manufacturersDrone: ManufacturerDrone[];
  loadManufacturersDrone: () => Promise<void>;

  manufacturersMeteorstation: Manufacturer[];
  loadManufacturersMeteorstation: () => Promise<void>;

  irrigationBrands: Manufacturer[];
  loadIrrigationBrands: () => Promise<void>;

  nutritionManufacturers: Manufacturer[];
  loadNutritionManufacturers: () => Promise<void>;

  defensives: ProtectionProduct[];
  loadDefensives: () => Promise<void>;

  inoculants: CatalogProduct[];
  loadInoculants: () => Promise<void>;

  biostimulants: CatalogProduct[];
  loadBiostimulants: () => Promise<void>;

  loadListNutrition: (id: string) => Promise<void>;
  loadListProtection: (id: string) => Promise<void>;
  setUser: (user: User) => void;
  setSubscription: (subscription: ISubscription) => void;
  setStep: (step: number) => void;
  setTerm: (term: boolean) => void;

  loadSubscriptions: () => Promise<void>;
  loadSubscription: (id: string) => Promise<void>;
  loadConfig: () => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;

  updateSubscription: (subscription: ISubscription) => void;
  clearSubscription: () => void;

  clearNutritionList: () => void;
  clearProtecionList: () => void;
  notAllowedNewSubscription: boolean;
};

export const useAppStore = create<AppDataStore>()(
  persist(
    (set, get) => ({
      subscription: {
        id: '',
        cod: 0,
        culture_type: '',
        current_step: 0,
        haverst: '',
        user_id: '',
        name_creator_sub: '',
        name_consultant: null,
        phone_consultant: null,
        tsr_id: null,
        producer_id: null,
        property_id: null,
        property_with_car_id: null,
        accept_terms: false,
        is_coupom: false,
        coupom_id: null,
        payment_id: null,
        term_id: null,
        seed_treatament_id: null,
        pre_sowing: undefined,
        sowing: undefined,
        coverage: undefined,
        leaf: undefined,
        specialties: undefined,
        herbicide: undefined,
        insecticide_caterpillars: undefined,
        insecticide_cicadas: undefined,
        fungicide: undefined,
        biodefencive: undefined,
        appointment_id: null,
        status: 'OPEN',
        user: null,
        tsrepresentative: null,
        tecnologies_subscription: null,
        producer: null,
        property: null,
        property_with_car: null,
        coupom: null,
        payment: null,
        term: null,
        hybridies_subscription: [],
        seed_treatment: null,
        protections: [],
        nutritions: [],
        appointment: null,
        created_at: undefined,
        updated_at: undefined,
      },
      listNutrition: [],
      listProtection: [],
      itemNutritionSelected: {} as Nutrition,
      itemProtectionSelected: {} as Protection,
      subscriptions: [],
      notAllowedNewSubscription: false,
      user: {} as User,
      isLoading: true,
      hybrids: [],
      manufacturers: [],
      nutritionManufacturers: [],
      manufacturersDrone: [],
      manufacturersMeteorstation: [],
      irrigationBrands: [],
      defensives: [],
      inoculants: [],
      biostimulants: [],
      config: {
        configSub: true,
        configSorgoSub: true,
        configPix: false,
        configCard: false,
        configCupom: true,
        safra: 'VERÃO 2025',
      },

      setUser: (user: User) => {
        set({ user });
      },

      setTerm: (term: boolean) => {
        const { subscription } = get();
        set({
          subscription: {
            ...subscription,
            accept_terms: term,
          },
        });
      },

      setStep: (step: number) => {
        const { subscription } = get();
        set({
          subscription: {
            ...subscription,
            current_step: step,
          },
        });
      },

      setCultureType: (cultureType: string) => {
        const { subscription } = get();
        set({
          subscription: {
            ...subscription,
            culture_type: cultureType,
          },
        });
      },

      clearSubscription: () => {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('@GetapAPP:subscription_id');
        }
        set({
          subscription: {
            id: '',
            cod: 0,
            culture_type: '',
            current_step: 0,
            haverst: '',
            user_id: '',
            name_creator_sub: '',
            name_consultant: null,
            phone_consultant: null,
            tsr_id: null,
            producer_id: null,
            property_id: null,
            property_with_car_id: null,
            accept_terms: false,
            is_coupom: false,
            coupom_id: null,
            payment_id: null,
            tecnologies_subscription: null,
            term_id: null,
            seed_treatament_id: null,
            pre_sowing: undefined,
            sowing: undefined,
            coverage: undefined,
            leaf: undefined,
            specialties: undefined,
            herbicide: undefined,
            insecticide_caterpillars: undefined,
            insecticide_cicadas: undefined,
            fungicide: undefined,
            biodefencive: undefined,
            appointment_id: null,
            status: 'OPEN',
            user: null,
            tsrepresentative: null,
            producer: null,
            property: null,
            property_with_car: null,
            coupom: null,
            payment: null,
            term: null,
            hybridies_subscription: [],
            seed_treatment: null,
            protections: [],
            nutritions: [],
            appointment: null,
            created_at: undefined,
            updated_at: undefined,
          },
        });
      },

      setSubscription: (subscription: ISubscription) => {
        set({ subscription });
      },

      setIsLoading: (isLoading: boolean) => {
        set({ isLoading });
      },

      loadSubscriptions: async () => {
        set({ isLoading: true });
        const res = await api.get('/users/subscriptions');

        set({
          subscriptions: res.data,
          isLoading: false,

          notAllowedNewSubscription: res.data.some(
            (eachStatus: ISubscription) => eachStatus.status === 'OPEN'
          ),
        });
      },

      loadConfig: async () => {
        set({ isLoading: true });
        const { data } = await api.get('/system');
        set({
          isLoading: false,
          config: {
            configSorgoSub: data.is_sorgo_subscription,
            configSub: data.is_subscription,
            configPix: data.pix_payment,
            configCard: data.card_payment,
            configCupom: data.cupom,
            safra: data.harvest,
          },
        });
      },

      loadHybrids: async (culture_type: string) => {
        set({ isLoading: true });
        const res = await api.get(`/system/hybrids?culture_type=${culture_type}`);
        set({
          isLoading: false,
          hybrids: res.data,
        });
      },

      loadManufacturers: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/manufacturers');
        set({
          isLoading: false,
          manufacturers: res.data,
        });
      },

      loadManufacturersDrone: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/manufacturers-drone');
        set({
          isLoading: false,
          manufacturersDrone: res.data,
        });
      },
      loadManufacturersMeteorstation: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/manufacturers-meteostation');
        set({
          isLoading: false,
          manufacturersMeteorstation: res.data,
        });
      },
      loadIrrigationBrands: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/manufacturers-irrigation');
        set({
          isLoading: false,
          irrigationBrands: res.data,
        });
      },

      loadNutritionManufacturers: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/nutritions');
        set({
          isLoading: false,
          nutritionManufacturers: res.data,
        });
      },

      loadDefensives: async () => {
        set({ isLoading: true });
        const res = await api.get('/system/defensives');
        set({
          isLoading: false,
          defensives: res.data,
        });
      },

      loadInoculants: async () => {
        const res = await api.get('/system/inoculants');
        set({
          inoculants: res.data,
        });
      },

      loadBiostimulants: async () => {
        const res = await api.get('/system/biostimulants');
        set({
          biostimulants: res.data,
        });
      },

      loadSubscription: async (id) => {
        set({ isLoading: true });
        const res = await api.get(`/subscription/${id}`);

        const { data } = await api.get(
          `/system/hybrids?culture_type=${res.data.culture_type}`
        );

        set({
          subscription: res.data,
          isLoading: false,
          hybrids: data,
        });
      },

      updateSubscription: (subscription: ISubscription) => {
        set({ subscription });
      },

      loadListNutrition: async (id: string) => {
        set({ isLoading: true });
        const res = await api.get(`/subscription/list-nutrition/${id}`);

        const list = res.data.map((item: Nutrition) => {
          return {
            ...item,
            phenological_stage: item.phenological_stage.includes('[')
              ? item.phenological_stage
              : `["${item.phenological_stage}"]`,
          };
        });

        set({
          listNutrition: list,
          isLoading: false,
        });
      },

      loadListProtection: async (id: string) => {
        set({ isLoading: true });
        const res = await api.get(`/subscription/list-protection/${id}`);

        const list = res.data.map((item: Protection) => {
          return {
            ...item,
            vegetative_stage: item.vegetative_stage.includes('[')
              ? item.vegetative_stage
              : `["${item.vegetative_stage}"]`,
          };
        });

        set({
          listProtection: list,
          isLoading: false,
        });
      },

      setItemNutritionSelected: (item: Nutrition) => {
        set({
          itemNutritionSelected: item,
        });
      },

      setItemProtectionSelected: (item: Protection) => {
        set({
          itemProtectionSelected: item,
        });
      },

      clearNutritionList: () => {
        set({
          itemNutritionSelected: {} as Nutrition,
        });
      },

      clearProtecionList: () => {
        set({
          itemProtectionSelected: {} as Protection,
        });
      },
    }),
    {
      name: '@GetapAPP:store',
      partialize: (state) => ({
        subscription: state.subscription,
        user: state.user,
        config: state.config,
        hybrids: state.hybrids,
        manufacturers: state.manufacturers,
        manufacturersDrone: state.manufacturersDrone,
        manufacturersMeteorstation: state.manufacturersMeteorstation,
        irrigationBrands: state.irrigationBrands,
        nutritionManufacturers: state.nutritionManufacturers,
        defensives: state.defensives,
        inoculants: state.inoculants,
        biostimulants: state.biostimulants,
      }),
    }
  )
);
